import { useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { Check } from './ui'
import { report } from '../lib/report'

/**
 * The moment a signature is made.
 *
 * Everywhere else in Beseter, staying signed in is the right thing — a writer
 * should not be logged out between Sundays. Here it isn't: this is the one
 * action that decides who owns a song, and an unlocked phone is not proof of
 * anything. So fresh proof is asked for, verified server-side, and the database
 * refuses the signature without it.
 *
 * The proof is a code sent to the address on the account: the same factor used
 * to open the account. The edge function sign-authorize verifies it against the
 * live auth system, issues a single-use grant, and the RLS guard trigger on
 * song_cowriters refuses any signature without a valid grant.
 */
export default function SignatureGate({ cowriter, songTitle, onSigned, onCancel }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const typedName = name.trim()
  const matches = typedName.toLowerCase() === (cowriter.cowriter_name || '').trim().toLowerCase()

  /**
   * Ask for the code.
   *
   * shouldCreateUser is false because this address is already an account by
   * definition — and without it, a typo in the auth table would quietly mint a
   * new user instead of failing.
   */
  const requestCode = async () => {
    setBusy(true); setErr(null)
    try {
      const { data } = await supabase.auth.getUser()
      const email = data?.user?.email
      if (!email) throw new Error('No email on this account')

      const { error } = await supabase.auth.signInWithOtp({
        email, options: { shouldCreateUser: false },
      })
      if (error) throw error
      setSent(true)
    } catch (e) {
      setErr(readableError(e))
    } finally {
      setBusy(false)
    }
  }

  const sign = async () => {
    setBusy(true); setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('sign-authorize', {
        body: { cowriter_id: cowriter.id, code },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      const { error: e2 } = await supabase.from('song_cowriters')
        .update({ signed: true, signature_name: typedName })
        .eq('id', cowriter.id)
      if (e2) throw e2

      setCode('')
      await onSigned()
    } catch (e) {
      // A co-writer signature that won't land is a rights problem, not a UI
      // annoyance — the song cannot proceed and nobody upstream finds out.
      report(e, 'signature', { cowriter_id: cowriter?.id })
      setErr(readableError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sigpad">
      <p className="small" style={{ margin: '0 0 4px' }}>
        You're signing for <b>{cowriter.split_percentage}%</b>
        {songTitle ? <> of “{songTitle}”</> : null}.
      </p>
      <p className="small" style={{ margin: '0 0 14px' }}>
        Once this is made it can't be changed, and your share can't be moved.
      </p>

      <div className="sigline">{typedName || ' '}</div>

      <input className="input" style={{ marginTop: 14 }} value={name}
             placeholder="Type your full name"
             autoComplete="off" onChange={(e) => setName(e.target.value)} />
      {typedName && !matches && (
        <p className="small" style={{ marginTop: 6 }}>
          That doesn't match <b>{cowriter.cowriter_name}</b> — sign as the name on the split sheet.
        </p>
      )}

      {/*
        The code is only offered once the name matches. Sending it earlier would
        put an email in someone's inbox for a signature they cannot complete,
        and the codes expire — so an early one is usually a dead one by the time
        it is needed.
      */}
      {!sent ? (
        <>
          <button className="btn" style={{ marginTop: 12 }}
                  disabled={busy || !matches} onClick={requestCode}>
            {busy ? 'Sending…' : 'Email me a code to sign'}
          </button>
          <p className="small" style={{ marginTop: 6, textAlign: 'left' }}>
            Asked because this one carries weight. It proves the signature was
            yours, not just that a phone was unlocked.
          </p>
        </>
      ) : (
        <>
          <input className="input" style={{ marginTop: 10 }} value={code}
                 inputMode="numeric" autoComplete="one-time-code"
                 placeholder="Six-digit code"
                 onChange={(e) => setCode(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && matches && code) sign() }} />
          <p className="small" style={{ marginTop: 6, textAlign: 'left' }}>
            Sent to the address on your account.{' '}
            <button className="btn-plain" style={{ minHeight: 0, padding: 0 }}
                    disabled={busy} onClick={requestCode}>Send it again</button>
          </p>

          <button className="btn btn-primary" style={{ marginTop: 12 }}
                  disabled={busy || !matches || !code} onClick={sign}>
            {busy ? 'Signing…' : <><Check /> Sign for {cowriter.split_percentage}%</>}
          </button>
        </>
      )}

      {onCancel && (
        <button className="btn-plain" style={{ width: '100%', marginTop: 4 }}
                onClick={onCancel}>Not yet</button>
      )}
      {err && <p className="err">{err}</p>}
    </div>
  )
}
